'use client'

import { useState, useEffect, useMemo, Suspense } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import CakeCard from '@/components/cake/cake-card'
import { Search, X, SlidersHorizontal, Loader2 } from 'lucide-react'

interface CakeItem {
  id: number
  name: string
  description: string
  minPrice: number
  maxPrice: number
  image: string
  category: string
  style: string
  tags: string[]
  rating: number
  monthlySales: number
  isHot: boolean
  isNew: boolean
  isSeasonal: boolean
  isCustomizable: boolean
  sizes: string[]
}

function MenuContent() {
  const searchParams = useSearchParams()
  const categoryParam = searchParams.get('category')
  const sortParam = searchParams.get('sort')

  const [allCakes, setAllCakes] = useState<CakeItem[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [keyword, setKeyword] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || '')
  const [sortBy, setSortBy] = useState(sortParam === 'sales' ? 'sales' : sortParam === 'price' ? 'price' : sortParam === 'price-desc' ? 'price-desc' : sortParam === 'rating' ? 'rating' : '')
  const [showFilter, setShowFilter] = useState(false)

  useEffect(() => {
    fetch('/api/cakes?pageSize=100')
      .then(res => res.json())
      .then(data => {
        setAllCakes(data.items || [])
        const cats = [...new Set<string>((data.items || []).map((c: any) => c.category))]
        setCategories(cats)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // 当 URL 参数变化时更新筛选
  useEffect(() => {
    if (categoryParam) setSelectedCategory(categoryParam)
  }, [categoryParam])

  const results = useMemo(() => {
    let filtered = [...allCakes]

    if (keyword) {
      const kw = keyword.toLowerCase()
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(kw) ||
        c.description.toLowerCase().includes(kw) ||
        c.tags.some(t => t.includes(kw))
      )
    }
    if (selectedCategory) {
      filtered = filtered.filter(c => c.category === selectedCategory)
    }

    switch (sortBy) {
      case 'sales': filtered.sort((a, b) => b.monthlySales - a.monthlySales); break
      case 'price': filtered.sort((a, b) => a.minPrice - b.minPrice); break
      case 'price-desc': filtered.sort((a, b) => b.minPrice - a.minPrice); break
      case 'rating': filtered.sort((a, b) => b.rating - a.rating); break
      default: break
    }

    return filtered
  }, [allCakes, keyword, selectedCategory, sortBy])

  const clearFilters = () => {
    setSelectedCategory('')
    setKeyword('')
    setSortBy('')
  }

  return (
    <div className="min-h-screen pb-16 bg-gray-50">
      <TopBar />

      {/* 搜索栏 */}
      <div className="sticky top-12 z-30 bg-white px-4 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center bg-gray-100 rounded-full px-3 h-8">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索蛋糕..."
              className="flex-1 bg-transparent text-sm ml-2 outline-none placeholder:text-gray-400"
            />
            {keyword && (
              <button onClick={() => setKeyword('')}>
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilter(!showFilter)}
            className={`relative flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium transition-colors ${showFilter || selectedCategory ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            筛选
            {selectedCategory && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">1</span>
            )}
          </button>
        </div>

        {/* 筛选面板 */}
        {showFilter && (
          <div className="mt-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-xs font-bold text-gray-700 mb-1.5">分类</p>
            <div className="flex flex-wrap gap-1.5">
              {categories.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
                  className={`px-2.5 py-1 text-xs rounded-full transition-colors ${selectedCategory === cat ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
                >{cat}</button>
              ))}
            </div>
            {selectedCategory && (
              <button onClick={clearFilters} className="text-xs text-pink-500 font-medium mt-2">
                清除筛选
              </button>
            )}
          </div>
        )}

        {/* 排序栏 */}
        <div className="flex items-center gap-3 mt-2">
          {[
            { key: '', label: '综合' },
            { key: 'sales', label: '销量' },
            { key: 'price', label: '价格↑' },
            { key: 'price-desc', label: '价格↓' },
            { key: 'rating', label: '评分' },
          ].map(opt => (
            <button key={opt.key} onClick={() => setSortBy(sortBy === opt.key ? '' : opt.key)}
              className={`text-xs font-medium pb-1 border-b-2 transition-colors ${sortBy === opt.key ? 'text-pink-500 border-pink-500' : 'text-gray-500 border-transparent'}`}
            >{opt.label}</button>
          ))}
          <span className="ml-auto text-xs text-gray-400">共{results.length}款</span>
        </div>
      </div>

      {/* 结果列表 */}
      {loading ? (
        <div className="flex items-center justify-center pt-16">
          <Loader2 className="w-6 h-6 text-pink-500 animate-spin" />
        </div>
      ) : results.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 px-4 mt-3">
          {results.map(cake => <CakeCard key={cake.id} cake={cake} />)}
        </div>
      ) : (
        <div className="col-span-2 text-center py-16 px-4">
          <p className="text-gray-400 text-sm">没有找到匹配的蛋糕</p>
          <button onClick={clearFilters} className="mt-2 text-xs text-pink-500">清除筛选</button>
        </div>
      )}

      <BottomNav />
    </div>
  )
}

export default function MenuPage() {
  return (
    <Suspense fallback={<div className="min-h-screen pb-16"><TopBar /><div className="pt-20 text-center text-sm text-gray-400">加载中...</div><BottomNav /></div>}>
      <MenuContent />
    </Suspense>
  )
}
